import { useCallback, useEffect, useRef, useState } from 'react';

import { useApp } from '../components/app-context';
import type { SettingInfo, UpdateSetting } from '../types/page';

const SETTINGS_UPDATE_DEBOUNCE_MS = 500;

export const useSettings = () => {
  const { api, showToast } = useApp();
  const [settings, setSettings] = useState<SettingInfo>();
  const mountedRef = useRef(true);
  const pendingUpdateRef = useRef<UpdateSetting>({});
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const updateQueueRef = useRef<Promise<void>>(Promise.resolve());

  const reportError = useCallback(
    (reason: unknown): void => {
      showToast(reason instanceof Error ? reason.message : String(reason), 'error');
    },
    [showToast],
  );

  const loadSettings = useCallback(async (): Promise<SettingInfo> => {
    try {
      const next = await api<SettingInfo>('/api/settings');
      if (mountedRef.current) {
        setSettings(next);
      }
      return next;
    } catch (reason) {
      reportError(reason);
      throw reason;
    }
  }, [api, reportError]);

  const updateSettings = useCallback(
    (values: UpdateSetting): Promise<SettingInfo> =>
      new Promise((resolve, reject) => {
        updateQueueRef.current = updateQueueRef.current
          .catch(() => undefined)
          .then(async () => {
            try {
              const next = await api<SettingInfo>('/api/settings', {
                method: 'PATCH',
                body: JSON.stringify(values),
              });
              if (mountedRef.current) {
                setSettings(next);
              }
              resolve(next);
            } catch (reason) {
              reportError(reason);
              reject(reason);
            }
          });
      }),
    [api, reportError],
  );

  const updateSettingsDebounced = useCallback(
    (values: UpdateSetting): void => {
      pendingUpdateRef.current = { ...pendingUpdateRef.current, ...values };
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = undefined;
        const pending = pendingUpdateRef.current;
        pendingUpdateRef.current = {};
        void updateSettings(pending).catch(() => undefined);
      }, SETTINGS_UPDATE_DEBOUNCE_MS);
    },
    [updateSettings],
  );

  const cancelSettingsUpdate = useCallback((...keys: Array<keyof UpdateSetting>): void => {
    for (const key of keys) {
      delete pendingUpdateRef.current[key];
    }
    if (Object.keys(pendingUpdateRef.current).length === 0 && debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = undefined;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    void loadSettings().catch(() => undefined);
    return () => {
      mountedRef.current = false;
    };
  }, [loadSettings]);

  return { settings, loadSettings, updateSettings, updateSettingsDebounced, cancelSettingsUpdate };
};
